import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import api from "../api";
import { Save, Upload, User, Palette, CreditCard } from "lucide-react";

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: "", business_name: "" });
  const [watermark, setWatermark] = useState({ text: "", opacity: 0.3 });
  const [branding, setBranding] = useState({
    primary_color: "#4f46e5",
    accent_color: "#ec4899",
    background_color: "#0f172a",
    logo_url: null,
    gallery_title: "",
    hide_storybook_badge: false,
  });
  const [payment, setPayment] = useState({
    stripe_publishable_key: "",
    stripe_secret_key: "",
    payment_instructions: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", business_name: user.business_name || "" });
    }
    loadWatermark();
    loadBranding();
    loadPayment();
  }, [user]);

  const loadWatermark = async () => {
    try {
      const res = await api.get("/watermark/preview");
      setWatermark(res.data);
    } catch {}
  };

  const loadBranding = async () => {
    try {
      const res = await api.get("/branding");
      setBranding((b) => ({ ...b, ...res.data }));
    } catch {}
  };

  const loadPayment = async () => {
    try {
      const res = await api.get("/payments/config");
      setPayment((p) => ({ ...p, ...res.data, stripe_secret_key: "" }));
    } catch {}
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put("/auth/me", form);
      updateUser(res.data);
      toast.success("Profil zaktualizowany");
    } catch {
      toast.error("Blad aktualizacji profilu");
    } finally {
      setSaving(false);
    }
  };

  const handleWatermarkSave = async () => {
    try {
      await api.put("/watermark/settings", {
        watermark_text: watermark.text || watermark.watermark_text,
        watermark_opacity: watermark.opacity || watermark.watermark_opacity,
      });
      toast.success("Watermark zapisany");
    } catch {
      toast.error("Blad zapisu watermarku");
    }
  };

  const handleWatermarkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/watermark/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Watermark przeslany");
      setWatermark((w) => ({ ...w, image_url: res.data.watermark_url }));
    } catch {
      toast.error("Blad przesylania");
    }
  };

  const handleBrandingSave = async () => {
    try {
      await api.put("/branding", branding);
      toast.success("Branding zapisany");
    } catch {
      toast.error("Blad zapisu brandingu");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/branding/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBranding((b) => ({ ...b, logo_url: res.data.logo_url }));
      toast.success("Logo przeslane");
    } catch {
      toast.error("Blad przesylania logo");
    }
  };

  const handlePaymentSave = async () => {
    try {
      const data = { payment_instructions: payment.payment_instructions };
      if (payment.stripe_publishable_key) data.stripe_publishable_key = payment.stripe_publishable_key;
      if (payment.stripe_secret_key) data.stripe_secret_key = payment.stripe_secret_key;
      await api.put("/payments/config", data);
      toast.success("Konfiguracja platnosci zapisana");
    } catch {
      toast.error("Blad zapisu platnosci");
    }
  };

  const wmText = watermark.watermark_text || watermark.text || "";
  const wmOpacity = watermark.watermark_opacity || watermark.opacity || 0.3;

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-slate-900">Ustawienia</h1>

      {/* Profile */}
      <form onSubmit={handleProfileSave} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="w-5 h-5" /> Dane konta
        </h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Imie i nazwisko</label>
          <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Nazwa firmy</label>
          <input type="text" value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input type="email" value={user?.email || ""} disabled className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-500" />
        </div>
        <button type="submit" disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Zapisywanie..." : "Zapisz profil"}
        </button>
      </form>

      {/* Watermark */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Watermark</h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tekst watermarku</label>
          <input type="text" value={wmText}
            onChange={(e) => setWatermark((w) => ({ ...w, text: e.target.value, watermark_text: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="np. Jan Kowalski Photography" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Przezroczystosc: {Math.round(wmOpacity * 100)}%</label>
          <input type="range" min="0.05" max="0.8" step="0.05" value={wmOpacity}
            onChange={(e) => setWatermark((w) => ({ ...w, opacity: parseFloat(e.target.value), watermark_opacity: parseFloat(e.target.value) }))}
            className="w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Obraz watermarku</label>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-sm">
            <Upload className="w-4 h-4" /> Wybierz plik
            <input type="file" accept="image/*" onChange={handleWatermarkUpload} className="hidden" />
          </label>
        </div>
        <div className="relative bg-slate-200 rounded-lg h-32 flex items-center justify-center overflow-hidden">
          <span className="text-slate-400 text-sm">Podglad</span>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ opacity: wmOpacity }}>
            <span className="text-white text-xl font-bold select-none" style={{ transform: "rotate(-30deg)", textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}>
              {wmText || "WATERMARK"}
            </span>
          </div>
        </div>
        <button onClick={handleWatermarkSave}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors">
          <Save className="w-4 h-4" /> Zapisz watermark
        </button>
      </div>

      {/* Branding */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Palette className="w-5 h-5" /> Branding galerii
        </h2>
        <p className="text-sm text-slate-500">Dostosuj wyglad publicznej galerii do swojej marki.</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Kolor glowny</label>
            <div className="flex items-center gap-2">
              <input type="color" value={branding.primary_color || "#4f46e5"}
                onChange={(e) => setBranding((b) => ({ ...b, primary_color: e.target.value }))}
                className="w-8 h-8 rounded border-0 cursor-pointer" />
              <input type="text" value={branding.primary_color || "#4f46e5"}
                onChange={(e) => setBranding((b) => ({ ...b, primary_color: e.target.value }))}
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Kolor akcent</label>
            <div className="flex items-center gap-2">
              <input type="color" value={branding.accent_color || "#ec4899"}
                onChange={(e) => setBranding((b) => ({ ...b, accent_color: e.target.value }))}
                className="w-8 h-8 rounded border-0 cursor-pointer" />
              <input type="text" value={branding.accent_color || "#ec4899"}
                onChange={(e) => setBranding((b) => ({ ...b, accent_color: e.target.value }))}
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Tlo galerii</label>
            <div className="flex items-center gap-2">
              <input type="color" value={branding.background_color || "#0f172a"}
                onChange={(e) => setBranding((b) => ({ ...b, background_color: e.target.value }))}
                className="w-8 h-8 rounded border-0 cursor-pointer" />
              <input type="text" value={branding.background_color || "#0f172a"}
                onChange={(e) => setBranding((b) => ({ ...b, background_color: e.target.value }))}
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tytul galerii (zamiast nazwy projektu)</label>
          <input type="text" value={branding.gallery_title || ""}
            onChange={(e) => setBranding((b) => ({ ...b, gallery_title: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="np. JK Photography Gallery" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Logo</label>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-sm">
              <Upload className="w-4 h-4" /> Upload logo
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
            {branding.logo_url && <img src={branding.logo_url} alt="Logo" className="h-8" />}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={branding.hide_storybook_badge || false}
            onChange={(e) => setBranding((b) => ({ ...b, hide_storybook_badge: e.target.checked }))}
            className="rounded border-slate-300" />
          Ukryj badge "Powered by StoryBook"
        </label>

        {/* Branding preview */}
        <div className="rounded-lg overflow-hidden border border-slate-200">
          <div className="p-3 flex items-center gap-2" style={{ backgroundColor: branding.background_color || "#0f172a" }}>
            {branding.logo_url && <img src={branding.logo_url} alt="" className="h-5" />}
            <span className="text-white text-sm font-semibold">{branding.gallery_title || "Moja Galeria"}</span>
          </div>
          <div className="flex gap-2 p-3" style={{ backgroundColor: branding.background_color || "#0f172a" }}>
            <div className="w-16 h-12 rounded" style={{ backgroundColor: branding.primary_color || "#4f46e5" }} />
            <div className="w-16 h-12 rounded" style={{ backgroundColor: branding.accent_color || "#ec4899" }} />
            <div className="w-16 h-12 rounded bg-slate-600" />
          </div>
        </div>

        <button onClick={handleBrandingSave}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors">
          <Save className="w-4 h-4" /> Zapisz branding
        </button>
      </div>

      {/* Payments */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CreditCard className="w-5 h-5" /> Platnosci online (Stripe)
        </h2>
        <p className="text-sm text-slate-500">Skonfiguruj Stripe aby przyjmowac platnosci kartami i BLIK/P24.</p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Publishable Key</label>
          <input type="text" value={payment.stripe_publishable_key || ""}
            onChange={(e) => setPayment((p) => ({ ...p, stripe_publishable_key: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="pk_live_..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Secret Key</label>
          <input type="password" value={payment.stripe_secret_key || ""}
            onChange={(e) => setPayment((p) => ({ ...p, stripe_secret_key: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder={payment.stripe_configured ? "••••••• (skonfigurowany)" : "sk_live_..."} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Instrukcje platnosci (dla klientow bez Stripe)</label>
          <textarea value={payment.payment_instructions || ""}
            onChange={(e) => setPayment((p) => ({ ...p, payment_instructions: e.target.value }))}
            rows={3}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-none"
            placeholder="np. Przelew na konto: 00 1234 5678 ..." />
        </div>
        <button onClick={handlePaymentSave}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors">
          <Save className="w-4 h-4" /> Zapisz platnosci
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
